import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Work Mode — tablet-side plumbing that has nothing to do with the database:
 * which station this device is bolted to, idle detection, and the badge
 * reader.
 */

const STATION_KEY = 'atlas:work:stationId';

export function getBoundStationId(): string | null {
  try {
    return window.localStorage.getItem(STATION_KEY);
  } catch {
    return null;
  }
}

export function setBoundStationId(id: string | null): void {
  try {
    if (id) window.localStorage.setItem(STATION_KEY, id);
    else window.localStorage.removeItem(STATION_KEY);
  } catch {
    /* private mode — the station will just have to be picked again */
  }
}

/** The workstation this tablet is bound to, kept in localStorage. */
export function useBoundStation(): [string | null, (id: string | null) => void] {
  const [id, setId] = useState<string | null>(() => getBoundStationId());
  const bind = useCallback((next: string | null) => {
    setBoundStationId(next);
    setId(next);
  }, []);
  return [id, bind];
}

/**
 * Calls `onIdle` after `timeoutMs` without any pointer / key / touch
 * activity. Used to sign a worker out when they walk away from the tablet.
 */
export function useIdleTimer(enabled: boolean, timeoutMs: number, onIdle: () => void): void {
  const cb = useRef(onIdle);
  cb.current = onIdle;
  useEffect(() => {
    if (!enabled) return;
    let timer: number | undefined;
    const arm = () => {
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(() => cb.current(), timeoutMs);
    };
    const events: Array<keyof WindowEventMap> = ['pointerdown', 'pointermove', 'keydown', 'touchstart', 'wheel'];
    for (const ev of events) window.addEventListener(ev, arm, { passive: true });
    arm();
    return () => {
      if (timer) window.clearTimeout(timer);
      for (const ev of events) window.removeEventListener(ev, arm);
    };
  }, [enabled, timeoutMs]);
}

/**
 * Keyboard-wedge badge reader. USB / Bluetooth RFID and NFC readers "type"
 * the fob UID as a burst of keystrokes (typically 8–14 hex characters in
 * under 100 ms) followed by Enter. Nobody types that fast, so a burst of
 * ≥ `minLength` characters with ≤ `maxGapMs` between them that ends in
 * Enter is treated as a scan and swallowed before it reaches any input.
 */
export function useBadgeScanner(
  enabled: boolean,
  onScan: (code: string) => void,
  opts: { minLength?: number; maxGapMs?: number } = {},
): void {
  const { minLength = 6, maxGapMs = 80 } = opts;
  const cb = useRef(onScan);
  cb.current = onScan;
  useEffect(() => {
    if (!enabled) return;
    let buffer = '';
    let lastAt = 0;
    const onKey = (e: KeyboardEvent) => {
      const now = performance.now();
      if (now - lastAt > maxGapMs) buffer = '';
      lastAt = now;
      if (e.key === 'Enter') {
        if (buffer.length >= minLength) {
          const code = buffer;
          buffer = '';
          e.preventDefault();
          e.stopPropagation();
          cb.current(code);
        }
        buffer = '';
        return;
      }
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        buffer += e.key;
        // While a burst is in flight, keep the characters out of any input.
        if (buffer.length >= 3) e.preventDefault();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [enabled, minLength, maxGapMs]);
}

/** Ticks once a second so live timers re-render. */
export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}
